import { yupResolver } from '@hookform/resolvers/yup';
import Refresh from '@mui/icons-material/Refresh';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  CompanyShareableType,
  getV1CompaniesCompanyIdSitesSiteIdContacts,
  MatchKeyMatchableType,
  ModelError_Item,
  MovingSiteType,
  TimeZone,
} from '@treadinc/horizon-api-spec';
import { convertLength } from '@turf/helpers';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { get, isNull } from 'lodash-es';
import {
  ComponentProps,
  forwardRef,
  Fragment,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  ControllerProps,
  FieldErrors,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import AutocompleteEquipmentTypesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteEquipmentTypesTypeahead';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import { PhoneCodeFlagInput } from '~components/FormFields/PhoneCodeFlagInput';
import { TextFormField } from '~components/FormFields/TextFormField';
import { LngLat } from '~components/Maps/interfaces';
import { GeofenceType, MarkerType } from '~components/Maps/v2/constants';
import { MapV2 } from '~components/Maps/v2/MapV2';
import { TreadMarkerProps } from '~components/Maps/v2/types';
import MatchKeysSection from '~components/MatchKeys/MatchKeysSection';
import { AddressOptionForNewSite } from '~components/Order/SiteSelection/types';
import {
  data as enums,
  geoFenceCircleOption,
  geofenceEquipmentOption,
  geofenceNoneOption,
  geofenceOptions,
  geofencePolygonOption,
  GeoFenceTypes,
  movingGeoFencesTagsOptions,
} from '~constants/enums';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { SupportedAddressProvider } from '~hooks/useAddress/models/AddressProvider';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import { useDepartment } from '~hooks/useDepartment/useDepartment';
import { Equipment, EquipmentTypeTypeahead } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { useGeocode } from '~hooks/useGeocode/useGeocode';
import { Geofence, Site } from '~hooks/useSites/models';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import SiteMaterialRatesSection from '~pages/Settings/Administration/Sites/components/SiteMaterialRatesSection';
import useCompanyQuery from '~queries/useCompanyQuery';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { appendPinDropToName } from '~utils/sites';
import { resolveSiteTimeZone } from '~utils/siteTimeZone';
import { lookupTimeZone } from '~utils/timeZoneLookup';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { useSiteFormV2 } from './hooks';
import { SITE_FORM_SCHEMA } from './schema';
import { getTimeZoneAbbreviation } from './siteTime';
import {
  describeSiteTimeZoneHint,
  describeWallClockInUserTime,
} from './siteTimeZoneHints';
import {
  ContactFormFields,
  SiteFormV2DirtyFields,
  SiteFormV2Fields,
  SiteFormV2StateChangeProps,
} from './types';
import {
  applyAutoPromoteToCircle,
  geofenceToItemNameAndId,
  getErrorMapping,
  isCircleOrEquipmentGeofenceType,
  recenterCircleOrEquipmentGeofenceForAddress,
  resolveLatLonForAddress,
} from './utils';
import { WallClockTimeInput } from './WallClockTimeInput';

const gap = 2;

const parseLatLonString = (value: string): LngLat | null => {
  if (!value) return null;
  const [latText, lngText] = value.split(',').map((part) => part.trim());
  if (!latText || !lngText) return null;
  const lat = Number.parseFloat(latText);
  const lng = Number.parseFloat(lngText);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const getDefaultCircleCenter = (site: Nullable<Site> | undefined): LngLat | null => {
  const geofence = site?.nextBillionGeofence;
  if (!geofence) return null;
  if (geofence.type !== GeoFenceTypes.CIRCLE) return null;
  if (geofence.circleCenter) {
    return { lat: geofence.circleCenter.lat, lng: geofence.circleCenter.lon };
  }
  return parseLatLonString(site?.latLon ?? '');
};

export interface SiteFormV2Handler {
  submit: (
    callback: (
      data: SiteFormV2Fields,
      dirtyFields: SiteFormV2DirtyFields,
    ) => void | Promise<void>,
  ) => void;
  resetForm: (callback?: () => void) => void;
}

interface SiteFormProps {
  defaultSite?: Nullable<Site>;
  addressOption?: Nullable<AddressOptionForNewSite>;
  company?: Nullable<CompanyBasic>;
  onFormStateChange: (props: SiteFormV2StateChangeProps) => void;
  isInline?: boolean;
  errors?: ModelError_Item[];
}

const EMPTY_CONTACT = {
  name: '',
  phone: '',
  isDeleted: false,
};

export const SiteFormV2 = forwardRef(function SiteFormV2(
  {
    addressOption,
    defaultSite,
    onFormStateChange,
    company,
    errors: errorsProp,
  }: SiteFormProps,
  ref: Ref<SiteFormV2Handler>,
) {
  // Null means that we are working with the primary geofence
  const [selectedAdditionalGeofence, setSelectedAdditionalGeofence] = useState<
    number | null
  >(null);
  const [inferredSiteTimeZone, setInferredSiteTimeZone] = useState<TimeZone | null>(null);
  const [connectedCompanies, setConnectedCompanies] = useState<CompanyBasic[]>([]);
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);
  const [isLoadingEquipment, setIsLoadingEquipment] = useState(false);

  // Radius for map: only update on blur so the circle doesn't redraw on every keystroke
  const [primaryRadiusFocused, setPrimaryRadiusFocused] = useState(false);
  const [committedPrimaryRadius, setCommittedPrimaryRadius] = useState<number | null>(
    null,
  );
  const [additionalRadiusFocusedIndex, setAdditionalRadiusFocusedIndex] = useState<
    number | null
  >(null);
  const [committedAdditionalRadii, setCommittedAdditionalRadii] = useState<
    Record<number, number | null>
  >({});

  const { getPlaces, getAddressItemByGoogleMapsPlaceId, encodeLngLat } = useGeocode({
    addressProvider: SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH,
  });
  const { getAdditionalGeoFencesForSite } = useSiteFormV2();
  const { getEquipmentByCompanyId } = useEquipment();
  const {
    getAllConnectedCompaniesByCompanyId,
    getAllConnectedCompaniesByCompanyIdTypeahead,
  } = useCompany();
  const {
    company: companyDetails,
    isLoading: isCompanyDetailsLoading,
    timeFormat: companyTimeFormat,
  } = useCompanyQuery({ companyId: company?.id });
  const { getCompanySharesByCompanyId } = useCompanyShares();

  const { userStore, companyAssetsStore } = useStores();
  const userCompanyId = getEffectiveUserCompanyId(userStore);
  const isFeet = userStore?.userCompany?.isFeet ?? false;
  const [equipmentOptions, setEquipmentOptions] = useState<Equipment[]>([]);

  const { loadDepartmentOptions } = useDepartment();
  const departmentOptions = companyAssetsStore.departments;

  useImperativeHandle(ref, () => ({
    submit(
      callBack: (
        data: SiteFormV2Fields,
        dirtyFields: SiteFormV2DirtyFields,
      ) => void | Promise<void>,
    ) {
      setSelectedAdditionalGeofence(null);
      handleSubmit((data) => callBack(data, formState.dirtyFields))();
    },
    resetForm(callBack?: () => void) {
      setSelectedAdditionalGeofence(null);
      reset();
      callBack?.();
    },
  }));

  const isReadOnly = !!defaultSite?.companyShare;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    getValues,
    trigger,
    formState,
  } = useForm<SiteFormV2Fields>({
    resolver: yupResolver(SITE_FORM_SCHEMA),
    mode: 'all',
    defaultValues: {
      company: company ? { legalName: company.legalName, id: company.id } : null,
      name: defaultSite?.name ?? null,
      siteType: defaultSite?.siteType ?? null,
      externalId: defaultSite?.externalId ?? null,
      notes: defaultSite?.notes ?? null,
      address: defaultSite?.address ?? null,
      latLon: defaultSite?.latLon ?? '',
      circleCenter: getDefaultCircleCenter(defaultSite),
      geofenceId: defaultSite?.nextBillionGeofence?.id ?? null,
      geofenceType: defaultSite?.nextBillionGeofence
        ? geofenceToItemNameAndId(defaultSite.nextBillionGeofence)
        : geofenceNoneOption,
      radius: defaultSite?.nextBillionGeofence?.circleRadius
        ? Number(
            Number(
              convertLength(
                defaultSite?.nextBillionGeofence?.circleRadius,
                'meters',
                'feet',
              ),
            ).toFixed(3),
          )
        : null,
      equipment:
        equipmentOptions.find(
          (item) => item.id === defaultSite?.movingSite?.equipment_id,
        ) ?? null,
      drawingCoordinates: [],
      additionalGeofences: [],
      department:
        departmentOptions.find((item) => item.id === defaultSite?.department?.id) ?? null,
      isPaverTracker:
        defaultSite?.movingSite?.type === MovingSiteType.PAVER_TRACKER_MOVING_SITE,
      remoteSyncEnabled: defaultSite?.movingSite?.remote_sync_enabled ?? true,
      contacts: [EMPTY_CONTACT],
      companyShares: [],
      openAt: defaultSite?.openAt ?? null,
      closeAt: defaultSite?.closeAt ?? null,
      timeZone: defaultSite?.timeZone ?? null,
      equipmentTypes:
        defaultSite?.equipmentTypes?.map((equipmentType) => ({
          _entity: new EquipmentTypeTypeahead(equipmentType.id, equipmentType.name),
          label: equipmentType.name,
          value: equipmentType.id,
        })) ?? [],
    },
    delayError: 500,
  });
  const { errors, isValid, isDirty } = formState;

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'additionalGeofences',
  });

  const watchedLatLon = watch('latLon');
  const watchedCircleCenter = watch('circleCenter');
  const geoCodedAddress = watch('address');
  const watchedGeofenceType = watch('geofenceType');
  const watchedRadius = watch('radius');
  const watchedEquipment = watch('equipment');
  const watchedDrawingCoordinates = watch('drawingCoordinates');
  const watchedAdditionalGeofences = watch('additionalGeofences');
  const watchedRemoteSyncEnabled = watch('remoteSyncEnabled');
  const watchedCompany = watch('company');
  const watchedOpenAt = watch('openAt');
  const watchedCloseAt = watch('closeAt');
  const userTimeZone = userStore.user?.timeZone ?? dayjs.tz.guess();
  const isSiteLocationNew = !defaultSite?.id || Boolean(formState.dirtyFields.latLon);
  const areHoursBeingSetOnZonelessSite =
    !defaultSite?.timeZone &&
    Boolean(formState.dirtyFields.openAt || formState.dirtyFields.closeAt);
  const companyTimeZone = companyDetails?.timeZone ?? null;
  const knownSiteTimeZone = isSiteLocationNew
    ? (inferredSiteTimeZone ?? companyTimeZone)
    : (defaultSite?.timeZone ?? inferredSiteTimeZone ?? companyTimeZone);
  const siteTimeZoneAbbreviation = knownSiteTimeZone
    ? getTimeZoneAbbreviation(knownSiteTimeZone, dayjs())
    : undefined;
  const siteTimeZoneHint = describeSiteTimeZoneHint(knownSiteTimeZone);
  const describeHoursInUserTime = (wallClock: string | null) =>
    knownSiteTimeZone
      ? describeWallClockInUserTime({
          siteTimeZone: knownSiteTimeZone,
          timeFormat: companyTimeFormat,
          userTimeZone,
          wallClock,
        })
      : undefined;

  const isPaverTracker =
    defaultSite?.movingSite?.type === MovingSiteType.PAVER_TRACKER_MOVING_SITE;

  const {
    fields: contactFields,
    append: appendContact,
    update: updateContact,
    remove: removeContact,
  } = useFieldArray({
    control,
    name: 'contacts',
    keyName: 'contactId',
  });

  const { fields: companySharesFields, append: appendCompanyShares } = useFieldArray({
    control,
    name: 'companyShares',
  });

  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const fetchContacts = async (companyId: string, siteId: string) => {
    setIsLoadingContacts(true);
    const response = await getV1CompaniesCompanyIdSitesSiteIdContacts({
      path: { 'company-id': companyId, 'site-id': siteId },
    });

    const contacts = response.data.data.map((contact) => ({
      id: contact.id,
      name: contact.name || '',
      phone: contact.phone || '',
      isDeleted: false,
    })) as ContactFormFields[];

    if (contacts.length > 0) {
      setValue('contacts', contacts);
    }
    setIsLoadingContacts(false);
  };

  const previousCompanyIdRef = useRef(watchedCompany?.id);
  useEffect(() => {
    const companyId = watchedCompany?.id;
    if (companyId === previousCompanyIdRef.current) {
      return;
    }
    previousCompanyIdRef.current = companyId;

    // Equipment types belong to a company, so another company's picks cannot carry over.
    const hasSelectedEquipmentTypes = getValues('equipmentTypes').length > 0;
    if (hasSelectedEquipmentTypes) {
      setValue('equipmentTypes', [], { shouldDirty: true });
    }
  }, [getValues, setValue, watchedCompany?.id]);

  useEffect(() => {
    setInferredSiteTimeZone(null);
    const coordinates = parseLatLonString(watchedLatLon ?? '');
    if (!coordinates) {
      return;
    }

    let isLatestLookup = true;
    lookupTimeZone(coordinates.lat, coordinates.lng)
      .then((zone) => {
        if (isLatestLookup) {
          setInferredSiteTimeZone(zone);
        }
      })
      .catch((error) => {
        console.error('Failed to look up the site time zone:', error);
      });

    return () => {
      isLatestLookup = false;
    };
  }, [watchedLatLon]);

  useEffect(() => {
    const currentTimeZone = getValues('timeZone');
    const nextTimeZone = resolveSiteTimeZone({
      areHoursBeingSetOnZonelessSite,
      companyTimeZone,
      currentTimeZone,
      inferredTimeZone: inferredSiteTimeZone,
      isLocationNew: isSiteLocationNew,
    });
    if (nextTimeZone !== currentTimeZone) {
      setValue('timeZone', nextTimeZone, { shouldDirty: true });
    }
  }, [
    areHoursBeingSetOnZonelessSite,
    companyTimeZone,
    getValues,
    inferredSiteTimeZone,
    isSiteLocationNew,
    setValue,
  ]);

  useEffect(() => {
    if (defaultSite?.id && company?.id) {
      fetchContacts(company?.id, defaultSite.id);
    }
  }, [defaultSite, company?.id]);

  const handleContactDelete = (index: number) => {
    if (contactFields[index].id) {
      updateContact(index, {
        ...contactFields[index],
        isDeleted: true,
      });
    } else {
      removeContact(index);
    }
  };

  const resetLatLon = useCallback(() => {
    // Recenter the circle before validating latLon so the single schema
    // validation triggered below sees both updated values.
    recenterCircleOrEquipmentGeofenceForAddress(setValue, {
      geoCodedAddress,
      geofenceTypeId: watchedGeofenceType?.id,
    });
    resolveLatLonForAddress(setValue, {
      geoCodedAddress,
      defaultSiteLatLon: defaultSite?.latLon,
    });
  }, [geoCodedAddress, defaultSite?.latLon, watchedGeofenceType?.id, setValue]);

  useEffect(() => {
    // If the address changes, we need to reset the latLon
    // Only reset when geoCodedAddress is truthy (user selected an address),
    // not on mount where null !== undefined would trigger a premature validation error.
    if (geoCodedAddress && geoCodedAddress !== defaultSite?.address) {
      resetLatLon();
    }
  }, [geoCodedAddress]);

  useEffect(() => {
    // If the form state changes, we need to notify the parent component. This is from the first SiteForm, but
    // we should look into improving this.
    // isCompanyLoading also covers the additional-geofences hydration; the parent relies on
    // isLoadingSiteData to keep Save disabled until that finishes.
    onFormStateChange({
      isDirty,
      isValid,
      isLoadingSiteData: isCompanyLoading || isCompanyDetailsLoading,
    });
  }, [isValid, isDirty, isCompanyLoading, isCompanyDetailsLoading]);

  const previousEquipmentIdRef = useRef<string | undefined>(
    equipmentOptions.find((item) => item.id === defaultSite?.movingSite?.equipment_id)
      ?.id,
  );

  useEffect(() => {
    const equipmentId = watchedEquipment?.id;
    if (equipmentId === previousEquipmentIdRef.current) return;
    previousEquipmentIdRef.current = equipmentId;

    if (!isPaverTracker || !watchedEquipment) return;

    // Propagate equipment to additional equipment geofences
    watchedAdditionalGeofences.forEach((geofence, index) => {
      if (geofence.geofenceType?.id === GeoFenceTypes.EQUIPMENT) {
        setValue(`additionalGeofences.${index}.equipment`, watchedEquipment, {
          shouldDirty: true,
        });
      }
    });

    // Auto-update site name on equipment change
    const currentName = getValues('name') ?? '';
    const atIndex = currentName.indexOf(' @ ');
    const locationSuffix = atIndex !== -1 ? currentName.substring(atIndex + 3) : '';
    const equipmentName = watchedEquipment?.name ?? '';
    setValue(
      'name',
      `${t('form_fields.paver_tracker')}: ${equipmentName}${locationSuffix ? ` @ ${locationSuffix}` : ''}`,
      { shouldDirty: true },
    );
  }, [watchedEquipment?.id]);

  // Sync committed radius with form when field is not focused (so map stays in sync when value changes elsewhere)
  useEffect(() => {
    if (!primaryRadiusFocused) {
      setCommittedPrimaryRadius(watchedRadius ?? null);
    }
  }, [primaryRadiusFocused, watchedRadius]);
  useEffect(() => {
    if (additionalRadiusFocusedIndex === null) {
      setCommittedAdditionalRadii(
        Object.fromEntries(
          watchedAdditionalGeofences.map((g, i) => [i, g.radius ?? null]),
        ),
      );
    }
  }, [additionalRadiusFocusedIndex, watchedAdditionalGeofences]);

  useEffect(() => {
    const hydrateForm = async (siteId: string | null) => {
      if (!company?.id) {
        return;
      }

      setIsCompanyLoading(true);
      try {
        const emptyPromise = Promise.resolve(undefined);

        const equipmentPromise = (async () => {
          setIsLoadingEquipment(true);

          try {
            const { data } = await getEquipmentByCompanyId({
              companyId: company.id,
              shared: false,
            });
            setEquipmentOptions(data);

            return data;
          } catch {
            // Error is already handled in the hook
            return [];
          } finally {
            setIsLoadingEquipment(false);
          }
        })();
        const additionalGeofencesPromise = siteId
          ? getAdditionalGeoFencesForSite(company.id, siteId)
          : emptyPromise;
        const departmentPromise = loadDepartmentOptions(company.id);

        const [equipment, additionalGeofences, departments] = await Promise.all([
          equipmentPromise,
          additionalGeofencesPromise,
          departmentPromise,
        ]);

        const equipmentSelection = equipment?.find(
          (item) => item.id === defaultSite?.movingSite?.equipment_id,
        );
        if (equipmentSelection) {
          // loading the saved equipment is not a user change, so the paver-tracker rename skips it
          previousEquipmentIdRef.current = equipmentSelection.id;
          setValue('equipment', equipmentSelection);
        }
        const departmentSelection = departments?.find(
          (item) => item.id === defaultSite?.department?.id,
        );
        if (departmentSelection) {
          setValue('department', departmentSelection);
        }

        const additionalGeofencesFields = (additionalGeofences ?? []).map(
          (geofence: Geofence) => {
            const drawingCoordinates = geofence.geojson?.coordinates
              ? (geofence.geojson.coordinates as [number, number][][])[0].map(
                  (coordinate) => ({
                    lat: coordinate[1],
                    lng: coordinate[0],
                  }),
                )
              : geofence.circleCenter && geofence.circleRadius
                ? [
                    {
                      lat: geofence.circleCenter.lat,
                      lng: geofence.circleCenter.lon,
                    },
                  ]
                : [];
            const markerCenter =
              drawingCoordinates.length >= 3
                ? calculatePolygonCenter(drawingCoordinates)
                : (drawingCoordinates[0] ?? parseLatLonString(defaultSite?.latLon ?? ''));
            return {
              geofenceId: geofence.id,
              geofenceType: geofenceToItemNameAndId(geofence) ?? null,
              radius: geofence.circleRadius
                ? Number(
                    Number(
                      convertLength(geofence.circleRadius, 'meters', 'feet'),
                    ).toFixed(3),
                  )
                : null,
              equipment: equipmentSelection ?? null,
              drawingCoordinates,
              markerCenter,
              tag:
                movingGeoFencesTagsOptions.find((tag) => tag.id === geofence.tag) ?? null,
              department: departmentSelection ?? null,
            };
          },
        );
        setValue('additionalGeofences', additionalGeofencesFields);
      } finally {
        setIsCompanyLoading(false);
      }
    };
    hydrateForm(defaultSite?.id ?? null);
  }, [defaultSite?.id]);

  useEffect(() => {
    if (errorsProp) {
      for (const error of errorsProp) {
        const newErrorField = getErrorMapping(error.field, error.message);
        if (newErrorField.field && newErrorField.message) {
          setError(newErrorField.field as keyof ReturnType<typeof setError>, {
            type: 'manual',
            message: newErrorField.message,
          });
        }
      }
    }
  }, [errorsProp]);

  useEffect(() => {
    if (userCompanyId) {
      getAllConnectedCompaniesByCompanyId({
        companyId: userCompanyId,
        callback: setConnectedCompanies,
      });
    }
  }, [userCompanyId]);

  useEffect(() => {
    if (defaultSite?.id && userCompanyId) {
      getCompanySharesByCompanyId({
        companyId: userCompanyId,
        company_shareable_type: CompanyShareableType.SITE,
        company_shareable_id: defaultSite.id,
        sender_company_id: userCompanyId,
        callBack: (fetchedShares: CompanyShare[]) => {
          const companySharesValues = fetchedShares.map((companyShare) => {
            return {
              id: companyShare.id,
              receiverCompany: {
                id: companyShare.receiverCompany.id,
                legalName: companyShare.receiverCompany.legalName,
              },
              companyShareableId: companyShare.companyShareable.id,
              isDeleted: false,
            };
          });
          setValue('companyShares', companySharesValues);
        },
      });
    } else if (!defaultSite?.id) {
      setValue('companyShares', []);
    }
  }, [defaultSite?.id, userCompanyId]);

  // Default circle radius 100m (form stores feet)
  const defaultCircleRadiusFeet = useMemo(
    () => parseFloat(convertLength(100, 'meters', 'feet').toFixed(2)),
    [],
  );

  const previousGeofenceTypeIdRef = useRef<string | undefined>(undefined);
  /** When true, do not restore polygon from defaultSite (set on circle→polygon transition so cascading re-runs don't overwrite). */
  const skipPolygonRestorationRef = useRef(false);

  useEffect(() => {
    // If the geofence type changes, we need to clear the drawing coordinates
    if (
      watchedGeofenceType?.id !== GeoFenceTypes.POLYGON &&
      watchedDrawingCoordinates.length
    ) {
      setValue('drawingCoordinates', []);
    }
    if (
      watchedGeofenceType?.id !== GeoFenceTypes.CIRCLE &&
      watchedGeofenceType?.id !== GeoFenceTypes.EQUIPMENT &&
      !!watchedRadius
    ) {
      setValue('radius', null, { shouldValidate: true });
    }
    if (
      watchedGeofenceType?.id !== GeoFenceTypes.CIRCLE &&
      watchedGeofenceType?.id !== GeoFenceTypes.EQUIPMENT &&
      !isNull(watchedCircleCenter)
    ) {
      setValue('circleCenter', null, { shouldDirty: true, shouldValidate: true });
    }
    const isCircleOrEquipment = isCircleOrEquipmentGeofenceType(watchedGeofenceType?.id);
    const wasCircleOrEquipment =
      previousGeofenceTypeIdRef.current === GeoFenceTypes.CIRCLE ||
      previousGeofenceTypeIdRef.current === GeoFenceTypes.EQUIPMENT;
    if (wasCircleOrEquipment && watchedGeofenceType?.id === GeoFenceTypes.POLYGON) {
      skipPolygonRestorationRef.current = true;
    }
    if (watchedGeofenceType?.id !== GeoFenceTypes.POLYGON) {
      skipPolygonRestorationRef.current = false;
    }
    // Only apply default radius when first switching to circle/equipment; allow clearing after that
    if (
      isCircleOrEquipment &&
      !wasCircleOrEquipment &&
      (isNull(watchedRadius) || Number(watchedRadius) <= 0)
    ) {
      setValue('radius', defaultCircleRadiusFeet, { shouldValidate: true });
    }
    previousGeofenceTypeIdRef.current = watchedGeofenceType?.id;
    if (
      watchedGeofenceType?.id === GeoFenceTypes.POLYGON &&
      !skipPolygonRestorationRef.current &&
      defaultSite?.nextBillionGeofence?.type === GeoFenceTypes.POLYGON &&
      defaultSite?.nextBillionGeofence?.geojson?.coordinates.length
    ) {
      /*
       * I don't have time to fix it in this pass, but the model definition is incorrect
       * as an always empty list, so we cast here.
       */
      const coordinatesArr = defaultSite.nextBillionGeofence.geojson.coordinates as [
        number,
        number,
      ][][];
      const coordinates = coordinatesArr[0] ?? [];
      setValue(
        'drawingCoordinates',
        coordinates.map((coordinate) => ({
          lat: coordinate[1],
          lng: coordinate[0],
        })),
      );
    }
  }, [
    watchedGeofenceType,
    defaultSite,
    watchedRadius,
    watchedCircleCenter,
    defaultCircleRadiusFeet,
    setValue,
  ]);

  useEffect(() => {
    const isCircleOrEquipment = isCircleOrEquipmentGeofenceType(watchedGeofenceType?.id);
    if (!isCircleOrEquipment || !isNull(watchedCircleCenter)) {
      return;
    }
    const parsedLatLon = parseLatLonString(watchedLatLon);
    if (!parsedLatLon) {
      return;
    }
    setValue('circleCenter', parsedLatLon, { shouldDirty: true });
  }, [watchedGeofenceType?.id, watchedCircleCenter, watchedLatLon, setValue]);

  // Default triangle (~100m across) centered on a target coordinate for new polygons.
  const createDefaultTrianglePolygon = (lat: number, lng: number) => {
    const radiusDegrees = 0.001;
    return [
      { lat: lat + radiusDegrees, lng },
      {
        lat: lat - radiusDegrees / 2,
        lng: lng + (radiusDegrees * Math.sqrt(3)) / 2,
      },
      {
        lat: lat - radiusDegrees / 2,
        lng: lng - (radiusDegrees * Math.sqrt(3)) / 2,
      },
    ];
  };

  const calculatePolygonCenter = (coordinates: Array<LngLat>) => ({
    lat: coordinates.reduce((sum, coord) => sum + coord.lat, 0) / coordinates.length,
    lng: coordinates.reduce((sum, coord) => sum + coord.lng, 0) / coordinates.length,
  });

  const tagOptions = useMemo(() => {
    const selectedTagIds = new Set(
      watchedAdditionalGeofences
        .filter((geofence) => geofence.tag)
        .map((geofence) => geofence.tag?.id),
    );
    const unselectedTags = movingGeoFencesTagsOptions.filter((tag) => {
      return !selectedTagIds.has(tag.id);
    });
    return unselectedTags;
  }, [
    movingGeoFencesTagsOptions,
    watchedAdditionalGeofences.filter((geofence) => geofence.tag),
  ]);

  const initialMapCenter = useMemo(() => {
    if (addressOption?.lat && addressOption?.lng) {
      return {
        lat: addressOption.lat,
        lng: addressOption.lng,
      };
    }
    if (defaultSite?.lat && defaultSite?.lng) {
      return {
        lat: defaultSite.lat,
        lng: defaultSite.lng,
      };
    }
    if (companyDetails?.defaultLat && companyDetails?.defaultLon) {
      return {
        lat: companyDetails.defaultLat,
        lng: companyDetails.defaultLon,
      };
    }
  }, [
    addressOption?.lng,
    addressOption?.lat,
    defaultSite?.lng,
    defaultSite?.lat,
    companyDetails?.defaultLon,
    companyDetails?.defaultLat,
  ]);

  const isPrimaryCircleType =
    watchedGeofenceType?.id === GeoFenceTypes.CIRCLE ||
    watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT;
  const primaryCircleCenter = useMemo(() => {
    if (!isPrimaryCircleType) return null;
    if (watchedCircleCenter) {
      return watchedCircleCenter;
    }
    return parseLatLonString(watchedLatLon);
  }, [isPrimaryCircleType, watchedCircleCenter, watchedLatLon]);
  // Use committed radius for map when field is focused so circle doesn't redraw on every keystroke
  const effectivePrimaryRadius = primaryRadiusFocused
    ? committedPrimaryRadius
    : (watchedRadius ?? null);
  const primaryCircleRadiusMeters = useMemo(() => {
    if (!isPrimaryCircleType || !isNull(selectedAdditionalGeofence)) return null;
    const r = !isNull(effectivePrimaryRadius) ? Number(effectivePrimaryRadius) : NaN;
    if (Number.isNaN(r) || r <= 0) return null;
    return convertLength(r, 'feet', 'meters');
  }, [isPrimaryCircleType, selectedAdditionalGeofence, effectivePrimaryRadius]);

  // Content-based key so locationMarkers and allPolygonsExceptSelected update when any geofence data changes (even if array reference is stable)
  const additionalGeofencesContentKey = JSON.stringify(
    watchedAdditionalGeofences.map((g) => ({
      t: g.geofenceType?.id,
      r: g.radius,
      dc: g.drawingCoordinates,
      mc: g.markerCenter,
    })),
  );

  const allPolygonsExceptSelected = useMemo((): [number, number][][] => {
    if (Number.isInteger(selectedAdditionalGeofence)) {
      return [
        ...(watchedDrawingCoordinates.length > 0
          ? [
              watchedDrawingCoordinates.map((coordinate) => [
                coordinate.lng,
                coordinate.lat,
              ]) as [number, number][],
            ]
          : []),
        ...(watchedAdditionalGeofences
          .filter(
            (geofence, index) =>
              geofence.geofenceType?.id === GeoFenceTypes.POLYGON &&
              index !== selectedAdditionalGeofence,
          )
          .map((geofence) =>
            (geofence.drawingCoordinates ?? []).map((coordinate) => [
              coordinate.lng,
              coordinate.lat,
            ]),
          ) as [number, number][][]),
      ];
    }
    return watchedAdditionalGeofences
      .filter((geofence) => geofence.geofenceType?.id === GeoFenceTypes.POLYGON)
      .map((geofence) =>
        (geofence.drawingCoordinates ?? []).map((coordinate) => [
          coordinate.lng,
          coordinate.lat,
        ]),
      ) as [number, number][][];
  }, [
    selectedAdditionalGeofence,
    watchedDrawingCoordinates,
    additionalGeofencesContentKey,
  ]);

  // In future iterations, we will expose a TreadMarkerInterface for general consumption
  const locationMarkers:
    | Omit<TreadMarkerProps, 'onMouseUp' | 'onMouseDown'>[]
    | undefined = useMemo(() => {
    const siteCenter = parseLatLonString(watchedLatLon);
    if (!siteCenter) {
      return undefined;
    }
    const { lat, lng } = siteCenter;

    const primaryRadiusMeters =
      effectivePrimaryRadius && Number(effectivePrimaryRadius) > 0
        ? convertLength(Number(effectivePrimaryRadius), 'feet', 'meters')
        : 0;

    const markers: Omit<TreadMarkerProps, 'onMouseUp' | 'onMouseDown'>[] = [];

    // Primary circle marker: only primary radius (additional circles have their own markers)
    if (isPrimaryCircleType && primaryRadiusMeters > 0) {
      const circleCenter = primaryCircleCenter ?? siteCenter;
      markers.push({
        id: 'lat-lon-circle-marker',
        lat,
        lng,
        type:
          watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT
            ? MarkerType.moving_site
            : MarkerType.site,
        geofenceType: GeofenceType.circle,
        circleCenter,
        radii: [primaryRadiusMeters],
      } as Omit<TreadMarkerProps, 'onMouseUp' | 'onMouseDown'>);
    }

    // Polygon marker (polygons and dragging the site pin); click to select primary geofence.
    // zIndex above circles so the pin stays draggable when primary is a circle.
    markers.push({
      id: 'lat-lon-polygon-marker',
      lat,
      lng,
      type:
        watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT
          ? MarkerType.hidden_marker
          : MarkerType.site,
      draggable: !isReadOnly,
      onDragEnd: isReadOnly
        ? undefined
        : (newCoordinates: LngLat) => {
            setSelectedAdditionalGeofence(null);
            setValue('latLon', `${newCoordinates.lat},${newCoordinates.lng}`, {
              shouldDirty: true,
            });
          },
      onClick: () => setSelectedAdditionalGeofence(null),
      geofenceType: GeofenceType.polygon,
      coordinates: allPolygonsExceptSelected,
      zIndex: 1000,
    } as Omit<TreadMarkerProps, 'onMouseUp' | 'onMouseDown'>);

    // One stable marker per additional geofence (same id so no remount/drop on selection change)
    watchedAdditionalGeofences.forEach((geofence, index) => {
      const isCircle =
        geofence.geofenceType?.id === GeoFenceTypes.CIRCLE ||
        geofence.geofenceType?.id === GeoFenceTypes.EQUIPMENT;
      const effectiveRadius =
        additionalRadiusFocusedIndex === index
          ? (committedAdditionalRadii[index] ?? geofence.radius)
          : geofence.radius;
      const firstCoord = geofence.drawingCoordinates?.[0];
      const circleCenter = isCircle
        ? (firstCoord ?? geofence.markerCenter ?? siteCenter)
        : null;
      const polygonCenter = !isCircle
        ? (geofence.markerCenter ??
          (Array.isArray(geofence.drawingCoordinates) &&
          geofence.drawingCoordinates.length >= 3
            ? calculatePolygonCenter(geofence.drawingCoordinates)
            : siteCenter))
        : null;
      const center = isCircle
        ? (circleCenter ?? siteCenter)
        : (polygonCenter ?? siteCenter);
      const isSelected = index === selectedAdditionalGeofence;
      const showCircle =
        isCircle &&
        !isSelected &&
        !isNull(effectiveRadius) &&
        Number(effectiveRadius) > 0;
      markers.push({
        id: `additional-geofence-${index}`,
        lat: center.lat,
        lng: center.lng,
        type: MarkerType.site,
        ...(showCircle
          ? {
              geofenceType: GeofenceType.circle as const,
              circleCenter: center,
              radii: [convertLength(Number(effectiveRadius), 'feet', 'meters')],
              isAdditionalGeofence: true,
              additionalGeofenceIndex: index,
            }
          : { geofenceType: null }),
        draggable: !isReadOnly,
        onDragEnd: isReadOnly
          ? undefined
          : (newCoordinates: LngLat) => {
              setSelectedAdditionalGeofence(index);
              if (isCircle) {
                setValue(
                  `additionalGeofences.${index}.drawingCoordinates`,
                  [{ lat: newCoordinates.lat, lng: newCoordinates.lng }],
                  { shouldDirty: true },
                );
              }
              setValue(
                `additionalGeofences.${index}.markerCenter`,
                { lat: newCoordinates.lat, lng: newCoordinates.lng },
                { shouldDirty: true },
              );
            },
        onClick: () => setSelectedAdditionalGeofence(index),
        zIndex: 1000,
      } as unknown as Omit<TreadMarkerProps, 'onMouseUp' | 'onMouseDown'>);
    });

    return markers;
  }, [
    watchedLatLon,
    effectivePrimaryRadius,
    isPrimaryCircleType,
    primaryCircleCenter,
    selectedAdditionalGeofence,
    allPolygonsExceptSelected,
    additionalGeofencesContentKey,
    additionalRadiusFocusedIndex,
    committedAdditionalRadii,
    isReadOnly,
    watchedGeofenceType?.id,
  ]);

  const showAddMoreGeofenceButton = useMemo(() => {
    if (!watchedGeofenceType?.id) {
      return false;
    }
    if (watchedGeofenceType.id === geofenceNoneOption.id) {
      return false;
    }
    if (
      watchedGeofenceType.id === GeoFenceTypes.EQUIPMENT &&
      watchedAdditionalGeofences.length >= 1
    ) {
      return false;
    }
    if (watchedAdditionalGeofences.length >= 5) {
      return false;
    }
    return true;
  }, [watchedGeofenceType?.id, watchedAdditionalGeofences.length]);

  useEffect(() => {
    applyAutoPromoteToCircle(setValue, {
      watchedLatLon,
      defaultSiteLat: defaultSite?.lat,
      defaultSiteLng: defaultSite?.lng,
      watchedRadius,
      currentGeofenceTypeId: getValues('geofenceType')?.id,
      companySiteRadius: companyDetails?.siteRadius,
    });
  }, [watchedLatLon, companyDetails?.siteRadius, defaultSite?.lat, defaultSite?.lng]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap }}>
      <CompanySelectorFormField
        control={control}
        disabled={isReadOnly || !!defaultSite?.company?.id}
        errors={errors}
        name={'company'}
        sx={{ gridColumn: '1 / -1' }}
      />
      <Box
        sx={{
          alignContent: 'start',
          columnGap: gap,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        <TextFormField
          isRequired
          control={control}
          dataUi="page-settings-sites__site-form__name-input"
          disabled={isReadOnly}
          errors={errors}
          label={`${t('form_fields.name')}`}
          name="name"
          sx={{ mr: 2 }}
        />
        <AutocompleteFormField
          isRequired
          control={control}
          dataUi="page-settings-sites__site-form__site-type-input"
          disabled={isReadOnly}
          errors={errors}
          getLabel={(item) => item}
          getValue={(item) => item}
          label={`${t('form_fields.site_type')}`}
          list={enums.site_type.values}
          name="siteType"
        />
        <TextFormField
          control={control}
          disabled={isReadOnly}
          errors={errors}
          isRequired={false}
          label={`${t('form_fields.id')}`}
          name="externalId"
        />
        <AutocompleteFormField
          control={control}
          name="department"
          disabled={isReadOnly}
          errors={errors}
          list={departmentOptions}
          label={`${t('project.form.department')}`}
          getValue={(item) => item.id}
          getLabel={(item) => item.name || ''}
          clearable
        />
        <Box
          onClick={() => {
            setSelectedAdditionalGeofence(null);
          }}
          sx={(theme) => ({
            gridColumn: '1 / -1',
            alignContent: 'start',
            columnGap: gap,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            transition: 'all 0.5s',
            ...(isNull(selectedAdditionalGeofence) &&
            watchedAdditionalGeofences.length > 0
              ? { boxShadow: 3, p: 2, bgcolor: theme.semantic.chipBrandBg }
              : {}),
          })}
        >
          <Typography sx={{ gridColumn: '1 / -1', my: 2 }}>
            {t('form_fields.primary_geo_fence')}
          </Typography>
          <AutocompleteFormField
            isRequired
            control={control}
            disabled={
              isReadOnly ||
              watchedLatLon.length === 0 ||
              (watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT &&
                watchedAdditionalGeofences.length > 0)
            }
            errors={errors}
            getLabel={(item) => item?.name || ''}
            getValue={(item) => item?.id}
            label={`${t('form_fields.geo_fence_type')}`}
            list={[...geofenceOptions, geofenceNoneOption]}
            name="geofenceType"
            onSelect={(value) => {
              if (value?.id === GeoFenceTypes.POLYGON) {
                const latLon = parseLatLonString(watchedLatLon);
                if (latLon) {
                  setValue(
                    'drawingCoordinates',
                    createDefaultTrianglePolygon(latLon.lat, latLon.lng),
                    {
                      shouldDirty: true,
                      shouldValidate: true,
                    },
                  );
                }
              }
              trigger();
            }}
          />
          {watchedGeofenceType?.id === GeoFenceTypes.POLYGON && (
            <Typography
              sx={{
                color: 'text.secondary',
                mt: 1,
              }}
            >
              {t('form_fields.geo_fence_type_hint_v2')}
            </Typography>
          )}
          {watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT && (
            <AutocompleteFormField
              clearable={true}
              control={control}
              dataUi="page-settings-sites__site-form__equipment-input"
              disabled={
                isReadOnly ||
                (isPaverTracker
                  ? watchedRemoteSyncEnabled
                  : watchedAdditionalGeofences.length > 0)
              }
              errors={errors}
              getLabel={(item) => item._name || ''}
              getValue={(item) => item._id}
              label={`${t('form_fields.available_equipment')}`}
              loading={isLoadingEquipment || isCompanyLoading}
              list={equipmentOptions}
              name="equipment"
              noOptionsText={t('form_fields.no_options')}
            />
          )}
          {(watchedGeofenceType?.id === GeoFenceTypes.CIRCLE ||
            watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT) && (
            <Controller
              control={control}
              name="radius"
              render={({ field: { value, onChange, onBlur, ref } }) => {
                // Form stores radius in feet; show in user's unit without rounding (radiusInProperUnits rounds and causes drift)
                const displayValue =
                  value !== null && value !== undefined
                    ? isFeet
                      ? Number(value)
                      : Number(convertLength(Number(value), 'feet', 'meters').toFixed(2))
                    : null;
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      width: '100%',

                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  >
                    <FormFieldLabel
                      label={`${t('form_fields.radius')} (${isFeet ? t('units_of_distance.feet') : t('units_of_distance.meters')})`}
                    />
                    <TextField
                      inputRef={ref}
                      disabled={isReadOnly}
                      error={Boolean(get(errors, 'radius'))}
                      fullWidth
                      helperText={get(errors, 'radius')?.message}
                      margin="dense"
                      onBlur={() => {
                        setCommittedPrimaryRadius(watchedRadius ?? null);
                        setPrimaryRadiusFocused(false);
                        onBlur();
                      }}
                      onFocus={() => setPrimaryRadiusFocused(true)}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || v === null || v === undefined) {
                          onChange(null);
                          return;
                        }
                        const num = Number(v);
                        if (Number.isNaN(num)) return;
                        const newFeet = isFeet
                          ? num
                          : convertLength(num, 'meters', 'feet');
                        onChange(parseFloat(newFeet.toFixed(2)));
                      }}
                      size="small"
                      type="number"
                      value={displayValue ?? ''}
                      variant="outlined"
                    />
                  </Box>
                );
              }}
            />
          )}
          {watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT && isPaverTracker && (
            <Tooltip
              title={t('form_fields.use_paver_tracker_equipment_tooltip')}
              placement="bottom-start"
            >
              <span>
                <CheckBoxFormField
                  control={control}
                  name="remoteSyncEnabled"
                  label={t('form_fields.use_paver_tracker_equipment')}
                  disabled={isReadOnly}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '10px' } }}
                />
              </span>
            </Tooltip>
          )}
        </Box>
        {fields.length > 0 && (
          <Typography
            sx={{ gridColumn: '1 / -1', my: 2, pt: 2, borderTop: '1px solid lightgray' }}
          >
            {t('form_fields.additional_geo_fences')}
          </Typography>
        )}
        {fields.map((field, index) => {
          return (
            <Box
              key={field.id}
              data-test-id={`additional-geofence-${index}`}
              onClick={() => {
                setSelectedAdditionalGeofence(index);
              }}
              sx={(theme) => ({
                gridColumn: '1 / -1',
                border: '1px solid lightgray',
                display: 'grid',
                gap,
                gridTemplateColumns: '1fr 1fr',
                p: 2,
                position: 'relative',
                transition: 'all 0.5s',
                ...(selectedAdditionalGeofence === index
                  ? { boxShadow: 3, bgcolor: theme.semantic.chipBrandBg }
                  : {}),
              })}
            >
              {!isReadOnly && (
                <IconButton
                  disabled={isReadOnly}
                  sx={{ position: 'absolute', top: '-20px', right: '-20px' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!isNull(selectedAdditionalGeofence)) {
                      if (index === 0 || index === selectedAdditionalGeofence) {
                        setSelectedAdditionalGeofence(null);
                      }
                      if (index < selectedAdditionalGeofence) {
                        setSelectedAdditionalGeofence(selectedAdditionalGeofence - 1);
                      }
                    }
                    remove(index);
                  }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              )}
              <AutocompleteFormField
                control={control}
                disabled={
                  isReadOnly ||
                  watchedLatLon.length === 0 ||
                  watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT
                }
                errors={errors}
                getLabel={(item) => item?.name || ''}
                getValue={(item) => item?.id}
                label={`${t('form_fields.geo_fence_type')}`}
                list={
                  watchedAdditionalGeofences[index].geofenceType?.id ===
                  GeoFenceTypes.EQUIPMENT
                    ? [geofenceEquipmentOption]
                    : [geoFenceCircleOption, geofencePolygonOption]
                }
                name={`additionalGeofences.${index}.geofenceType`}
                onSelect={(value) => {
                  const g = watchedAdditionalGeofences[index];
                  const currentCenter =
                    g.markerCenter ??
                    (Array.isArray(g?.drawingCoordinates) &&
                    g.drawingCoordinates.length >= 3
                      ? calculatePolygonCenter(g.drawingCoordinates)
                      : (g?.drawingCoordinates?.[0] ?? parseLatLonString(watchedLatLon)));
                  if (!currentCenter) return;
                  setValue(`additionalGeofences.${index}.markerCenter`, currentCenter, {
                    shouldDirty: true,
                  });

                  if (value?.id === GeoFenceTypes.POLYGON) {
                    setValue(`additionalGeofences.${index}.radius`, null, {
                      shouldDirty: true,
                    });
                    setValue(
                      `additionalGeofences.${index}.drawingCoordinates`,
                      createDefaultTrianglePolygon(currentCenter.lat, currentCenter.lng),
                      { shouldDirty: true },
                    );
                  } else if (
                    value?.id === GeoFenceTypes.CIRCLE ||
                    value?.id === GeoFenceTypes.EQUIPMENT
                  ) {
                    setValue(
                      `additionalGeofences.${index}.drawingCoordinates`,
                      [{ lat: currentCenter.lat, lng: currentCenter.lng }],
                      { shouldDirty: true },
                    );
                    const currentRadius = g?.radius;
                    if (
                      currentRadius === null ||
                      currentRadius === undefined ||
                      Number(currentRadius) <= 0
                    ) {
                      setValue(
                        `additionalGeofences.${index}.radius`,
                        defaultCircleRadiusFeet,
                        { shouldDirty: true },
                      );
                    }
                  }
                }}
              />
              {watchedAdditionalGeofences[index].geofenceType?.id ===
                GeoFenceTypes.EQUIPMENT && (
                <AutocompleteFormField
                  clearable={true}
                  control={control}
                  disabled
                  errors={errors}
                  getLabel={(item) => item._name || ''}
                  getValue={(item) => item._id}
                  label={`${t('form_fields.available_equipment')}`}
                  loading={isLoadingEquipment || isCompanyLoading}
                  list={equipmentOptions}
                  name={`additionalGeofences.${index}.equipment`}
                  noOptionsText={t('form_fields.no_options')}
                />
              )}
              {(watchedAdditionalGeofences[index].geofenceType?.id ===
                GeoFenceTypes.CIRCLE ||
                watchedAdditionalGeofences[index].geofenceType?.id ===
                  GeoFenceTypes.EQUIPMENT) && (
                <Controller
                  control={control}
                  name={`additionalGeofences.${index}.radius`}
                  render={({ field: { value, onChange, onBlur, ref } }) => {
                    // Form stores radius in feet; show in user's unit without rounding (radiusInProperUnits rounds and causes drift)
                    const displayValue =
                      value !== null && value !== undefined
                        ? isFeet
                          ? Number(value)
                          : Number(
                              convertLength(Number(value), 'feet', 'meters').toFixed(2),
                            )
                        : null;
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          width: '100%',

                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                          },
                        }}
                      >
                        <FormFieldLabel
                          label={`${t('form_fields.radius')} (${isFeet ? t('units_of_distance.feet') : t('units_of_distance.meters')})`}
                        />
                        <TextField
                          inputRef={ref}
                          disabled={isReadOnly}
                          error={Boolean(
                            get(errors, `additionalGeofences.${index}.radius`),
                          )}
                          fullWidth
                          helperText={
                            get(errors, `additionalGeofences.${index}.radius`)?.message
                          }
                          margin="dense"
                          onBlur={() => {
                            setCommittedAdditionalRadii((prev) => ({
                              ...prev,
                              [index]: watchedAdditionalGeofences[index]?.radius ?? null,
                            }));
                            setAdditionalRadiusFocusedIndex(null);
                            onBlur();
                          }}
                          onFocus={() => setAdditionalRadiusFocusedIndex(index)}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '' || v === null || v === undefined) {
                              onChange(null);
                              return;
                            }
                            const num = Number(v);
                            if (Number.isNaN(num)) return;
                            const newFeet = isFeet
                              ? num
                              : convertLength(num, 'meters', 'feet');
                            onChange(parseFloat(newFeet.toFixed(2)));
                          }}
                          size="small"
                          type="number"
                          value={displayValue ?? ''}
                          variant="outlined"
                        />
                      </Box>
                    );
                  }}
                />
              )}
              <AutocompleteFormField
                clearable={true}
                control={control}
                disabled={isReadOnly}
                errors={errors}
                getLabel={(item) => item?.name || ''}
                getValue={(item) => item?.id}
                label={`${t('form_fields.tag')}`}
                list={tagOptions}
                name={`additionalGeofences.${index}.tag`}
              />
            </Box>
          );
        })}
        {showAddMoreGeofenceButton && !isReadOnly && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Button
              variant="text"
              color="info"
              size="small"
              disabled={!isValid}
              onClick={() => {
                const defaultRadiusFeet =
                  typeof companyDetails?.siteRadius === 'number'
                    ? Number(
                        convertLength(
                          companyDetails.siteRadius,
                          'meters',
                          'feet',
                        ).toFixed(2),
                      )
                    : 300;
                const markerCenter = parseLatLonString(watchedLatLon);
                append({
                  geofenceId: null,
                  geofenceType:
                    watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT
                      ? geofenceEquipmentOption
                      : geoFenceCircleOption,
                  radius:
                    watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT
                      ? null
                      : defaultRadiusFeet,
                  equipment:
                    watchedGeofenceType?.id === GeoFenceTypes.EQUIPMENT
                      ? watchedEquipment
                      : null,
                  drawingCoordinates: [],
                  markerCenter,
                  tag: null,
                });
                setSelectedAdditionalGeofence(fields.length);
              }}
              sx={{ textDecoration: 'underline' }}
            >
              {t('administration.site.add_geofence')}
            </Button>
          </Box>
        )}
        <Box
          sx={{
            columnGap: gap,
            display: 'grid',
            gridColumn: '1 / -1',
            gridTemplateColumns: '1fr 1fr',
            mt: 2,
          }}
        >
          <WallClockTimeInput
            control={control}
            dataUi="page-settings-sites__site-form__open-at-input"
            disabled={isReadOnly}
            errors={errors}
            label={t('administration.site.open_time')}
            name="openAt"
            note={describeHoursInUserTime(watchedOpenAt ?? null)}
            timeFormat={companyTimeFormat}
            timeZoneAbbreviation={siteTimeZoneAbbreviation}
          />
          <WallClockTimeInput
            control={control}
            dataUi="page-settings-sites__site-form__close-at-input"
            disabled={isReadOnly}
            errors={errors}
            label={t('administration.site.close_time')}
            name="closeAt"
            note={describeHoursInUserTime(watchedCloseAt ?? null)}
            timeFormat={companyTimeFormat}
            timeZoneAbbreviation={siteTimeZoneAbbreviation}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', gridColumn: '1 / -1', mt: 1 }}
        >
          {siteTimeZoneHint}
        </Typography>
        <Box sx={{ gridColumn: '1 / -1', mt: 2 }}>
          <AutocompleteEquipmentTypesTypeahead
            companyId={watchedCompany?.id || company?.id || userCompanyId}
            control={control}
            isDisabled={isReadOnly}
            isMultiple
            label={t('administration.site.accepted_equipment_type')}
            name="equipmentTypes"
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('administration.site.equipment_types_empty_means_all')}
          </Typography>
        </Box>
        <TextFormField
          control={control}
          disabled={isReadOnly}
          errors={errors}
          label={`${t('form_fields.notes')}`}
          multiline={true}
          name="notes"
          rows={4}
          sx={{ gridColumn: '1 / -1', mt: 2 }}
        />

        <Box
          sx={{
            gridColumn: '1 / -1',
            alignContent: 'start',
            columnGap: gap,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr auto',
          }}
        >
          <Typography sx={{ gridColumn: '1 / -1', my: 2 }}>
            {t('administration.site.contacts')}
          </Typography>

          {isLoadingContacts ? (
            <Box
              sx={{
                gridColumn: '1 / -1',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {contactFields.map(
                (contact, index) =>
                  !contact.isDeleted && (
                    <Fragment key={index}>
                      <TextFormField
                        control={control}
                        disabled={isReadOnly}
                        errors={errors}
                        label={`${t('form_fields.name')}`}
                        name={`contacts.${index}.name`}
                        sx={{ mr: 2 }}
                      />
                      <PhoneCodeFlagInput
                        control={control as unknown as ControllerProps['control']}
                        disabled={isReadOnly}
                        errors={errors as unknown as FieldErrors}
                        name={`contacts.${index}.phone`}
                        label={`${t('form_fields.phone')}`}
                      />
                      {!isReadOnly && (
                        <IconButton
                          disabled={isReadOnly}
                          onClick={() => handleContactDelete(index)}
                          sx={{ alignSelf: 'center', mt: 2, p: 0 }}
                        >
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      )}
                    </Fragment>
                  ),
              )}

              {!isReadOnly && (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Button
                    variant="text"
                    color="info"
                    size="small"
                    disabled={isReadOnly}
                    onClick={() => appendContact(EMPTY_CONTACT)}
                    sx={{ textDecoration: 'underline' }}
                  >
                    {t('administration.site.add_contact')}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>

        {!isReadOnly && (
          <Box
            sx={{
              gridColumn: '1 / -1',
              alignContent: 'start',
              columnGap: gap,
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                gridColumn: '1 / -1',
                mt: 2,
              }}
            >
              {t('administration.equipment.share_with_connected_companies')}
            </Typography>
            <Typography variant="body2">
              {t('administration.site.share_with_connected_companies_desc')}
            </Typography>
            {companySharesFields
              .map((item, index) => ({ item, index }))
              .filter(({ index }) => !watch(`companyShares.${index}.isDeleted`))
              .map(({ item, index }) => {
                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      mt: 2,
                    }}
                  >
                    <Box sx={{ width: '100%', mr: 2 }}>
                      <AutocompleteAsyncFormField
                        control={control}
                        errors={errors}
                        name={`companyShares[${index}].receiverCompany`}
                        getValue={(item) => item.id}
                        getLabel={(item) => item.legalName}
                        asyncCallback={getAllConnectedCompaniesByCompanyIdTypeahead}
                        extraRequestOptions={{
                          companyId: company?.id ?? userCompanyId,
                        }}
                        label={`${t('form_fields.share_to_company')}`}
                        disabled={isReadOnly || !!item.receiverCompany?.id}
                      />
                    </Box>
                    <RemoveItemButton
                      disabled={isReadOnly}
                      action={() => {
                        setValue(`companyShares.${index}.isDeleted`, true, {
                          shouldDirty: true,
                        });
                      }}
                      sx={{
                        ml: 1,
                        mt: 2.3,
                        cursor: isReadOnly ? 'not-allowed' : 'pointer',
                      }}
                    />
                  </Box>
                );
              })}

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Button
                variant="text"
                color="info"
                size="small"
                disabled={
                  isReadOnly || companySharesFields.length === connectedCompanies.length
                }
                onClick={() => {
                  companySharesFields.length !== connectedCompanies.length &&
                    appendCompanyShares({
                      id: '',
                      receiverCompany: { id: '', legalName: '' },
                      companyShareableId: defaultSite?.id || '',
                      isDeleted: false,
                    });
                }}
                sx={{ textDecoration: 'underline' }}
              >
                {t('administration.equipment.add_shared_company')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap,
          gridTemplateColumns: '1fr auto auto',
          gridTemplateRows: 'auto 500px 1fr',
        }}
      >
        <AutocompleteAsyncFormField
          asyncCallback={getPlaces}
          clearable={true}
          control={control}
          debounceTime={500}
          disabled={isReadOnly}
          errors={errors}
          getInputValue={(item) => {
            if (item.fullAddress) {
              return item.fullAddress;
            }

            const isFullAddress = item._thoroughfare.includes(',');
            return isFullAddress
              ? `${item.shortName} ${item._locality}, ${item._administrative_area} ${item._postal_code}`
              : `${item._thoroughfare} ${item._locality}, ${item._administrative_area} ${item._postal_code}`;
          }}
          getLabel={(item) => get(item, 'nameWithAddress', '')}
          getValue={(item) => get(item, 'placeId', '')}
          onSelect={async (selectedAddress: AddressItem) => {
            return await getAddressItemByGoogleMapsPlaceId(
              selectedAddress.placeId,
              selectedAddress.fullAddress ?? undefined,
            );
          }}
          placeholder={`${t('common.address_autocomplete_hint')}`}
          label={`${t('form_fields.address')}`}
          name="address"
        />
        <TextFormField
          isRequired
          control={control}
          dataUi="page-settings-sites__site-form__lat-lon-input"
          disabled={isReadOnly}
          errors={errors}
          label={`${t('form_fields.coordinates')}`}
          name="latLon"
          placeholder={`${t('form_fields.lat_lon_placeholder')}`}
          type="text"
        />
        <Tooltip title={t('administration.site.set_position_from_address')}>
          <Button
            color="inherit"
            size="small"
            disabled={isReadOnly}
            onClick={resetLatLon}
            sx={{ p: 0, mt: '18px', height: 40 }}
          >
            <Refresh sx={{ width: 14, height: 14 }} />
          </Button>
        </Tooltip>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <MapV2
            center={initialMapCenter}
            markers={locationMarkers as ComponentProps<typeof MapV2>['markers']}
            drawingMode={
              isNull(selectedAdditionalGeofence)
                ? watchedGeofenceType?.id === GeoFenceTypes.POLYGON
                : watchedAdditionalGeofences[selectedAdditionalGeofence].geofenceType
                    ?.id === GeoFenceTypes.POLYGON
            }
            drawingCoordinates={
              isNull(selectedAdditionalGeofence)
                ? watchedDrawingCoordinates
                : watchedAdditionalGeofences[selectedAdditionalGeofence]
                    .drawingCoordinates
            }
            emptyText={`${t('navigation.choose_address')}`}
            onClick={
              isReadOnly
                ? undefined
                : (position) => {
                    setValue('latLon', `${position.lat},${position.lng}`, {
                      shouldDirty: true,
                    });
                    const currentName = watch('name') || '';
                    setValue('name', appendPinDropToName(currentName), {
                      shouldDirty: true,
                    });
                    encodeLngLat(`${position.lat},${position.lng}`)
                      .then((address) => {
                        if (address) {
                          setValue('address', address, { shouldDirty: true });
                        }
                      })
                      .catch(() => {});
                  }
            }
            onPolygonEdit={
              isReadOnly
                ? undefined
                : (newCoordinates) => {
                    if (isNull(selectedAdditionalGeofence)) {
                      setValue('drawingCoordinates', newCoordinates, {
                        shouldDirty: true,
                      });
                    } else {
                      update(selectedAdditionalGeofence, {
                        ...watchedAdditionalGeofences[selectedAdditionalGeofence],
                        drawingCoordinates: newCoordinates,
                      });
                    }
                  }
            }
            editableCircleCenter={
              isReadOnly
                ? null
                : !isNull(selectedAdditionalGeofence)
                  ? (() => {
                      const g = watchedAdditionalGeofences[selectedAdditionalGeofence];
                      const isCircle =
                        g?.geofenceType?.id === GeoFenceTypes.CIRCLE ||
                        g?.geofenceType?.id === GeoFenceTypes.EQUIPMENT;
                      if (!isCircle || !g?.radius) return null;
                      const firstCoord = g.drawingCoordinates?.[0];
                      if (
                        firstCoord !== null &&
                        firstCoord !== undefined &&
                        Number.isFinite(firstCoord.lat) &&
                        Number.isFinite(firstCoord.lng)
                      ) {
                        return firstCoord;
                      }
                      return g.markerCenter ?? parseLatLonString(watchedLatLon);
                    })()
                  : primaryCircleCenter
            }
            editableCircleRadius={
              isReadOnly
                ? null
                : !isNull(selectedAdditionalGeofence)
                  ? (() => {
                      const g = watchedAdditionalGeofences[selectedAdditionalGeofence];
                      const isCircle =
                        g?.geofenceType?.id === GeoFenceTypes.CIRCLE ||
                        g?.geofenceType?.id === GeoFenceTypes.EQUIPMENT;
                      if (!isCircle || isNull(g?.radius)) return null;
                      const effectiveR =
                        additionalRadiusFocusedIndex === selectedAdditionalGeofence
                          ? (committedAdditionalRadii[selectedAdditionalGeofence] ??
                            g.radius)
                          : g.radius;
                      if (isNull(effectiveR)) return null;
                      return convertLength(Number(effectiveR), 'feet', 'meters');
                    })()
                  : primaryCircleRadiusMeters
            }
            onEditableCircleChange={
              isReadOnly
                ? undefined
                : !isNull(selectedAdditionalGeofence)
                  ? (center, radiusMeters) => {
                      update(selectedAdditionalGeofence, {
                        ...watchedAdditionalGeofences[selectedAdditionalGeofence],
                        drawingCoordinates: [{ lat: center.lat, lng: center.lng }],
                        markerCenter: { lat: center.lat, lng: center.lng },
                        radius: parseFloat(
                          convertLength(radiusMeters, 'meters', 'feet').toFixed(2),
                        ),
                      });
                    }
                  : (center, radiusMeters) => {
                      setValue(
                        'circleCenter',
                        { lat: center.lat, lng: center.lng },
                        { shouldDirty: true },
                      );
                      const radiusFeet = convertLength(radiusMeters, 'meters', 'feet');
                      setValue('radius', parseFloat(radiusFeet.toFixed(2)), {
                        shouldDirty: true,
                      });
                    }
            }
            editableCircleReplacesPrimaryMarker={isNull(selectedAdditionalGeofence)}
            selectedCircleMarkerId={
              isNull(selectedAdditionalGeofence)
                ? 'lat-lon-circle-marker'
                : watchedAdditionalGeofences[selectedAdditionalGeofence]?.geofenceType
                      ?.id === GeoFenceTypes.CIRCLE ||
                    watchedAdditionalGeofences[selectedAdditionalGeofence]?.geofenceType
                      ?.id === GeoFenceTypes.EQUIPMENT
                  ? `additional-geofence-${selectedAdditionalGeofence}`
                  : ''
            }
          />
        </Box>
      </Box>

      {/* Create mode has no site to price yet, so the rates panel only renders for
          a persisted site. */}
      {defaultSite?.id ? (
        <SiteMaterialRatesSection
          companyId={company?.id ?? userCompanyId}
          site={defaultSite}
        />
      ) : null}

      <MatchKeysSection
        companyId={company?.id ?? userCompanyId}
        matchableId={defaultSite?.id}
        matchableType={MatchKeyMatchableType.SITE}
        recordName={defaultSite?.name ?? ''}
      />
    </Box>
  );
});
